import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';

const ReadingPart1 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      // 1. Prioritize API data in review mode
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      // 2. Otherwise load from localStorage
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData) {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);


  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);

    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const exampleQuestion = exerciseData.questions.find(q => q.order_index === 0);
  const mainQuestions = exerciseData.questions.filter(q => q.order_index !== 0).sort((a, b) => a.order_index - b.order_index);

  const handleAnswerChange = (questionId, event) => {
    setAnswers(prev => ({ ...prev, [questionId]: event.target.value }));
  };

  return (
    <div className="w-full h-full p-2 md:p-3">
      <div className="relative flex flex-col h-full bg-transparent dark:from-slate-800/50 dark:to-card-dark rounded-2xl p-3 md:p-4 overflow-y-auto custom-scrollbar">
        <div className="w-full m-auto max-w-2xl">

          {/* Example question */}
          {exampleQuestion && (
            <div className="mb-4 bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200/60 dark:border-slate-600 p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <span className="shrink-0 font-black text-amber-500 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/50 px-2 py-0.5 rounded-lg text-sm border border-gray-200">e.g.</span>
                <p className="exercise-body-text flex-grow m-0 text-sm">{exampleQuestion.question_text}</p>
                <div className="flex-shrink-0">
                  <input
                    type="text"
                    value={exampleQuestion.accepted_answers[0]}
                    disabled
                    className="exercise-input-field exercise-input-text exercise-example-text w-36 text-center"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Main questions */}
          <div className="space-y-3">
            {mainQuestions.map((q) => {
              const isAnswerCorrect = q.is_correct === true || q.is_correct === 'true';
              const showIncorrectAnswerInReview = isReviewMode && !isAnswerCorrect;

              return (
                <div key={q.id} className="bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-3 md:p-4 shadow-sm hover:border-gray-300 transition-colors flex items-center gap-3">
                  <span className="font-black text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/50 px-2 py-0.5 rounded-lg text-sm border border-gray-200 shrink-0">
                    {q.order_index}.
                  </span>
                  <p className="exercise-body-text flex-grow m-0 text-sm">{q.question_text}</p>
                  <div className="flex-shrink-0 flex items-center gap-1">
                    {showIncorrectAnswerInReview ? (
                      <div className="exercise-input-text inline-flex items-center bg-red-50 px-2 py-1 rounded-lg border border-red-100">
                        <span style={{ textDecoration: 'line-through' }} className="text-red-500 opacity-70 mr-2">{q.user_answer?.answer || '(empty)'}</span>
                        <span className="text-green-600 font-bold">{q.accepted_answers[0]}</span>
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={showHint || (isReviewMode && isAnswerCorrect) ? q.accepted_answers[0] : answers[q.id] || ''}
                        onChange={(e) => handleAnswerChange(q.id, e)}
                        className={`exercise-input-field exercise-input-text w-36 text-center ${showHint ? 'hint-input' : ''}`}
                        readOnly={showHint}
                        disabled={isReviewMode || !isHydrated}
                        autoComplete="off"
                      />
                    )}
                    {isReviewMode && (isAnswerCorrect
                      ? <span className="correct-checkmark ml-1">✓</span>
                      : <span className="incorrect-checkmark ml-1">✗</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </div>
  );
};


export default ReadingPart1;
